import type { ApiClient } from "@/api/client";
import { API_VERSION_PREFIX, newIdempotencyKey } from "@/api/client";
import type {
  ActivityEntry,
  AdminListResponse,
  BulkResult,
  BulkTaskAction,
  ListParams,
  Me,
  RequestMeta,
  StaffMember,
  TaskAction,
  TaskDto,
  TaskLinkDto,
  TaskSummary,
} from "./types";

const BASE = `${API_VERSION_PREFIX}/hq/admin`;

function toQuery(params: object = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "" || value === false) continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function createTasksApi(client: ApiClient) {
  return {
    listTasks(params: ListParams = {}) {
      return client.get<AdminListResponse<TaskDto>>(`${BASE}/tasks${toQuery(params)}`);
    },
    getTask(id: string) {
      return client.get<{ task: TaskDto; meta: RequestMeta }>(`${BASE}/tasks/${id}`);
    },
    createTask(body: Record<string, unknown>) {
      return client.post<{ task: TaskDto; meta: RequestMeta }>(
        `${BASE}/tasks`,
        body,
        newIdempotencyKey(),
      );
    },
    patchTask(id: string, body: Record<string, unknown>) {
      return client.patch<{ task: TaskDto; meta: RequestMeta }>(
        `${BASE}/tasks/${id}`,
        body,
        newIdempotencyKey(),
      );
    },
    applyTaskAction(id: string, action: TaskAction, body: Record<string, unknown>) {
      return client.post<{ task: TaskDto }>(
        `${BASE}/tasks/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },
    bulkAction(body: {
      task_ids: string[];
      action: BulkTaskAction;
      owner_user_id?: string | null;
      outcome?: string | null;
      reason?: string;
    }) {
      return client.post<BulkResult>(`${BASE}/task-bulk`, body, newIdempotencyKey());
    },
    summary() {
      return client.get<TaskSummary>(`${BASE}/task-summary`);
    },
    me() {
      return client.get<Me>(`${BASE}/me`);
    },
    staff() {
      return client.get<{ items: StaffMember[] }>(`${BASE}/staff`);
    },
    listComments(taskId: string) {
      return client.get<{ comments: { id: string; body: string; author_name: string | null; created_at: string }[] }>(
        `${BASE}/tasks/${taskId}/comments`,
      );
    },
    addComment(taskId: string, body: string) {
      return client.post(`${BASE}/tasks/${taskId}/comments`, { body }, newIdempotencyKey());
    },
    activity(taskId: string) {
      return client.get<{ activity: ActivityEntry[] }>(`${BASE}/tasks/${taskId}/activity`);
    },
    listTaskLinks(taskId: string) {
      return client.get<{ links: TaskLinkDto[]; meta: RequestMeta }>(
        `${BASE}/tasks/${taskId}/links`,
      );
    },
    createTaskLink(taskId: string, body: Record<string, unknown>) {
      return client.post(`${BASE}/tasks/${taskId}/links`, body, newIdempotencyKey());
    },
    deleteTaskLink(taskId: string, linkId: string) {
      return client.request<{ deleted: boolean; id: string; meta: RequestMeta }>(
        `${BASE}/tasks/${taskId}/links/${linkId}`,
        { method: "DELETE", idempotencyKey: newIdempotencyKey() },
      );
    },
  };
}

export type TasksApi = ReturnType<typeof createTasksApi>;
